"use client";

import { type ReactNode, useRef, useState } from "react";

import type { ContextAction } from "../types";
import { useActions } from "./useActions";

export type UseContextActionsProps = {
  /**
   * The list of context actions
   */
  actions: ContextAction[];
  /**
   * The title to display in the confirmation modal
   */
  title?: ReactNode;
  /**
   * Callback when an action is triggered
   */
  onAction?: (action: ContextAction) => void;
};

export type UseContextActionsReturn = {
  /**
   * Handler for when an action is clicked
   */
  handleActionClick: (action: ContextAction) => void;
  /**
   * Whether the delete confirmation modal is active
   */
  isConfirmationActive: boolean;
  /**
   * Set the confirmation modal active state
   */
  setIsConfirmationActive: (active: boolean) => void;
  /**
   * Handler for confirming the delete action
   */
  handleDeleteConfirm: () => void;
  /**
   * The title to display in the confirmation modal
   */
  confirmationTitle: ReactNode;
};

/**
 * Hook that provides shared logic for context actions.
 * Handles action execution and delete confirmation flow.
 */
export const useContextActions = ({
  title,
  onAction,
}: UseContextActionsProps): UseContextActionsReturn => {
  const { handleAction } = useActions();

  const [isConfirmationActive, setIsConfirmationActive] = useState(false);
  const confirmationCallbackRef = useRef<() => void>(() => {});

  const handleActionClick = (action: ContextAction) => {
    if (action?.type === "delete" && action?.action) {
      setIsConfirmationActive(true);
      confirmationCallbackRef.current = () => {
        if (action.action) {
          handleAction(action.action);
          onAction?.(action);
        }
      };
    } else if (action?.action) {
      onAction?.(action);
      handleAction(action.action);
    }
  };

  const handleDeleteConfirm = () => {
    setIsConfirmationActive(false);
    confirmationCallbackRef.current();
  };

  return {
    handleActionClick,
    isConfirmationActive,
    setIsConfirmationActive,
    handleDeleteConfirm,
    confirmationTitle: title,
  };
};
